import React, { useState, useEffect, useCallback, useRef } from 'react';
import DOMPurify from 'dompurify';
import { api } from '../../api/client';
import { useContacts } from '../../contexts/ContactsContext';

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const now = new Date();
  const diffH = (now - d) / 3600000;
  if (diffH < 24 && d.toDateString() === now.toDateString()) {
    return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  }
  if (diffH < 168) {
    return d.toLocaleDateString('en-GB', { weekday: 'short', hour: '2-digit', minute: '2-digit' });
  }
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

function stripHtml(html) {
  return (html || '').replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
}

// ── CRM Contact Card ──────────────────────────────────────────────────────────

function ContactCard({ fromEmail, fromName, onAddToCrm }) {
  const { contacts } = useContacts();
  const contact = contacts.find(c => c.email?.toLowerCase() === fromEmail?.toLowerCase());

  if (!contact) {
    return (
      <div style={styles.contactCard}>
        <span style={{ fontSize: '0.78rem', color: '#888' }}>
          {fromName || fromEmail} — not in CRM
        </span>
        <button onClick={onAddToCrm} style={styles.addCrmBtn}>
          + Add to CRM
        </button>
      </div>
    );
  }

  const healthColor = contact.healthScore >= 70 ? '#1e6645' : contact.healthScore >= 40 ? '#7a4a10' : '#A12710';
  const healthBg = contact.healthScore >= 70 ? '#eaf6ef' : contact.healthScore >= 40 ? '#fff8ee' : '#fde8e8';

  return (
    <div style={styles.contactCard}>
      <div style={{ fontWeight: 700, fontSize: '0.88rem', color: '#0C2D48' }}>{contact.name}</div>
      {contact.company && <div style={{ fontSize: '0.78rem', color: '#666' }}>{contact.company}</div>}
      <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.25rem', flexWrap: 'wrap' }}>
        {contact.healthScore != null && (
          <span style={{ fontSize: '0.72rem', background: healthBg, color: healthColor, borderRadius: 6, padding: '0.1rem 0.45rem', fontWeight: 700 }}>
            Health {contact.healthScore}
          </span>
        )}
        {contact.lastContactedAt && (
          <span style={{ fontSize: '0.72rem', color: '#888' }}>
            Last contact: {formatDate(contact.lastContactedAt)}
          </span>
        )}
      </div>
    </div>
  );
}

// ── Triage Bar ────────────────────────────────────────────────────────────────

const TRIAGE_META = [
  { key: 'urgent',      label: 'Urgent',      color: '#A12710', bg: '#fde8e8' },
  { key: 'actionable',  label: 'Actionable',  color: '#7a4a10', bg: '#fff8ee' },
  { key: 'fyi',         label: 'FYI',         color: '#2a6080', bg: '#e8f4fb' },
  { key: 'newsletters', label: 'Newsletters', color: '#555',    bg: '#f0f0f0' },
];

function TriageBar({ emails, triageFilter, onFilter }) {
  const counts = {
    urgent:      emails.filter(e => e.triageCategory === 'urgent').length,
    actionable:  emails.filter(e => e.triageCategory === 'actionable').length,
    fyi:         emails.filter(e => e.triageCategory === 'fyi').length,
    newsletters: emails.filter(e => e.triageCategory === 'newsletters').length,
  };
  const hasAny = Object.values(counts).some(c => c > 0);
  if (!hasAny) return null;

  return (
    <div style={styles.triageBar}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
        <span style={{ fontSize: '0.72rem', fontWeight: 700, color: '#aaa', flexShrink: 0 }}>Filter:</span>
        {TRIAGE_META.filter(({ key }) => counts[key] > 0).map(({ key, label, color, bg }) => (
          <button
            key={key}
            onClick={() => onFilter(triageFilter === key ? null : key)}
            style={{
              ...styles.triageChip,
              background: triageFilter === key ? color : bg,
              color: triageFilter === key ? '#fff' : color,
              fontWeight: triageFilter === key ? 700 : 600,
            }}
          >
            {counts[key]} {label}
          </button>
        ))}
        {triageFilter && (
          <button onClick={() => onFilter(null)} style={{ ...styles.triageChip, background: '#eee', color: '#555' }}>
            ✕ Clear filter
          </button>
        )}
      </div>
    </div>
  );
}

// ── Reply Composer ────────────────────────────────────────────────────────────

function ReplyComposer({ email, onSend, onClose, onWriteWithAidey, sending, initialBody = '', initialSubject }) {
  const [to, setTo] = useState(email.fromEmail || email.from || '');
  const [subject, setSubject] = useState(
    initialSubject || (email.subject?.startsWith('Re:') ? email.subject : `Re: ${email.subject || ''}`)
  );
  const [body, setBody] = useState(initialBody);

  return (
    <div style={styles.replyComposer}>
      <div style={styles.replyHeader}>
        <span style={{ fontWeight: 700, fontSize: '0.9rem' }}>Reply</span>
        <button onClick={onClose} style={styles.closeBtn}>✕</button>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', padding: '0 0.9rem 0.6rem' }}>
        <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
          <span style={styles.replyLabel}>To</span>
          <input
            value={to}
            onChange={e => setTo(e.target.value)}
            style={styles.replyInput}
          />
        </div>
        <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
          <span style={styles.replyLabel}>Subject</span>
          <input
            value={subject}
            onChange={e => setSubject(e.target.value)}
            style={styles.replyInput}
          />
        </div>
        <textarea
          value={body}
          onChange={e => setBody(e.target.value)}
          placeholder="Write your reply…"
          rows={5}
          style={styles.replyTextarea}
        />
        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-start', flexWrap: 'wrap', marginTop: '0.25rem', paddingRight: '80px' }}>
          <button
            onClick={() => onWriteWithAidey({ to, subject, body, email })}
            style={styles.aideyDraftBtn}
          >
            ✍️ Write with Aidey
          </button>
          <button onClick={onClose} style={styles.cancelBtn}>Cancel</button>
          <button
            onClick={() => onSend({ to, subject, body, email })}
            disabled={sending || !to || !body}
            style={{ ...styles.sendBtn, opacity: (sending || !to || !body) ? 0.6 : 1 }}
          >
            {sending ? 'Sending…' : 'Send'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Aidey Draft Card ─────────────────────────────────────────────────────────

// ── Compose (new email) modal ─────────────────────────────────────────────────
function ComposeModal({ connections, onSend, onClose, sending }) {
  const accounts = connections.filter(c => ['gmail', 'outlook', 'imap'].includes(c.provider));
  const [fromId, setFromId] = useState(() => {
    const def = accounts.find(a => a.isDefault) || accounts[0];
    return def ? def.id : null;
  });
  const [to, setTo] = useState('');
  const [showCcBcc, setShowCcBcc] = useState(false);
  const [cc, setCc] = useState('');
  const [bcc, setBcc] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [err, setErr] = useState('');
  const [improving, setImproving] = useState(false);
  const [preImprove, setPreImprove] = useState(null); // snapshot for Undo

  const from = accounts.find(a => a.id === fromId);

  async function improve() {
    if (!body.trim()) { setErr('Write a rough draft first, then Improve.'); return; }
    setImproving(true); setErr('');
    const snapshot = { subject, body };
    try {
      const res = await api('/api/email/improve', {
        method: 'POST',
        body: JSON.stringify({ subject, body, to }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Could not improve the draft.');
      setPreImprove(snapshot);
      if (data.body) setBody(data.body);
      // Only replace the subject when the sender's was empty/weak (per spec).
      const weakSubject = !snapshot.subject.trim() || snapshot.subject.trim().length < 4;
      if (weakSubject && data.subject) setSubject(data.subject);
    } catch (e) {
      setErr(e.message);
    } finally {
      setImproving(false);
    }
  }

  function undoImprove() {
    if (!preImprove) return;
    setSubject(preImprove.subject);
    setBody(preImprove.body);
    setPreImprove(null);
  }

  function submit() {
    if (!from) { setErr('Choose an account to send from.'); return; }
    if (!to.trim()) { setErr('Add at least one recipient.'); return; }
    if (!subject.trim()) { setErr('Add a subject.'); return; }
    setErr('');
    onSend({ connection: from, to: to.trim(), cc: cc.trim(), bcc: bcc.trim(), subject: subject.trim(), body });
  }

  return (
    <div style={composeStyles.overlay} onClick={onClose}>
      <div style={composeStyles.modal} onClick={e => e.stopPropagation()}>
        <div style={composeStyles.header}>
          <span style={{ fontWeight: 800, fontSize: '1rem', color: '#0C2D48' }}>New email</span>
          <button onClick={onClose} style={composeStyles.closeBtn} title="Close">✕</button>
        </div>

        <div style={composeStyles.row}>
          <span style={composeStyles.label}>From</span>
          <select value={fromId || ''} onChange={e => setFromId(Number(e.target.value))} style={composeStyles.input}>
            {accounts.map(a => (
              <option key={a.id} value={a.id}>
                {(a.label ? a.label + ' — ' : '') + a.email}{a.isDefault ? '  (default)' : ''}
              </option>
            ))}
          </select>
        </div>

        <div style={composeStyles.row}>
          <span style={composeStyles.label}>To</span>
          <input value={to} onChange={e => setTo(e.target.value)} placeholder="name@example.com, another@example.com" style={composeStyles.input} />
        </div>

        {!showCcBcc ? (
          <button onClick={() => setShowCcBcc(true)} style={composeStyles.ccToggle}>+ Cc/Bcc</button>
        ) : (
          <>
            <div style={composeStyles.row}>
              <span style={composeStyles.label}>Cc</span>
              <input value={cc} onChange={e => setCc(e.target.value)} placeholder="Comma-separated" style={composeStyles.input} />
            </div>
            <div style={composeStyles.row}>
              <span style={composeStyles.label}>Bcc</span>
              <input value={bcc} onChange={e => setBcc(e.target.value)} placeholder="Comma-separated" style={composeStyles.input} />
            </div>
          </>
        )}

        <div style={composeStyles.row}>
          <span style={composeStyles.label}>Subject</span>
          <input value={subject} onChange={e => setSubject(e.target.value)} style={composeStyles.input} />
        </div>

        <textarea value={body} onChange={e => setBody(e.target.value)} placeholder="Write your message…" style={composeStyles.textarea} />

        <div style={composeStyles.improveRow}>
          <button onClick={improve} disabled={improving} style={composeStyles.improveBtn}>
            {improving ? '✨ Improving…' : '✨ Improve with Aidey'}
          </button>
          {preImprove && (
            <button onClick={undoImprove} disabled={improving} style={composeStyles.undoBtn}>↺ Undo</button>
          )}
        </div>

        {err && <div style={composeStyles.error}>{err}</div>}

        <div style={composeStyles.footer}>
          <button onClick={onClose} style={composeStyles.cancelBtn}>Cancel</button>
          <button onClick={submit} disabled={sending} style={composeStyles.sendBtn}>
            {sending ? 'Sending…' : 'Send'}
          </button>
        </div>
      </div>
    </div>
  );
}

const composeStyles = {
  overlay: { position: 'fixed', inset: 0, background: 'rgba(12,45,72,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1rem' },
  modal: { background: '#fff', borderRadius: 14, width: '100%', maxWidth: 560, maxHeight: '90vh', overflowY: 'auto', padding: '1.25rem 1.35rem', boxShadow: '0 12px 40px rgba(0,0,0,0.25)', fontFamily: "'Raleway', sans-serif" },
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.9rem' },
  closeBtn: { background: 'none', border: 'none', fontSize: '1.1rem', cursor: 'pointer', color: '#888', lineHeight: 1 },
  row: { display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.6rem' },
  label: { width: 58, flexShrink: 0, fontSize: '0.78rem', fontWeight: 700, color: '#0C2D48', textTransform: 'uppercase', letterSpacing: 0.4 },
  input: { flex: 1, minWidth: 0, border: '1px solid #e0e0e0', borderRadius: 8, padding: '0.5rem 0.65rem', fontSize: '0.88rem', fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box' },
  ccToggle: { background: 'none', border: 'none', color: '#5352ed', fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer', padding: '0 0 0.5rem 62px', textAlign: 'left' },
  textarea: { width: '100%', minHeight: 180, border: '1px solid #e0e0e0', borderRadius: 8, padding: '0.65rem', fontSize: '0.9rem', fontFamily: 'inherit', resize: 'vertical', outline: 'none', boxSizing: 'border-box', lineHeight: 1.5, marginTop: '0.3rem' },
  error: { color: '#e05252', fontSize: '0.82rem', marginTop: '0.5rem' },
  improveRow: { display: 'flex', alignItems: 'center', gap: '0.6rem', marginTop: '0.5rem' },
  improveBtn: { background: '#f4f3ff', border: '1px solid #c5c2f5', color: '#5352ed', borderRadius: 8, padding: '0.4rem 0.8rem', cursor: 'pointer', fontFamily: 'inherit', fontWeight: 700, fontSize: '0.8rem' },
  undoBtn: { background: 'none', border: 'none', color: '#888', cursor: 'pointer', fontFamily: 'inherit', fontWeight: 600, fontSize: '0.8rem' },
  footer: { display: 'flex', justifyContent: 'flex-end', gap: '0.6rem', marginTop: '0.9rem' },
  cancelBtn: { background: 'transparent', border: '1px solid #d1d5db', borderRadius: 8, padding: '0.5rem 1.1rem', cursor: 'pointer', fontFamily: 'inherit', fontWeight: 600, fontSize: '0.85rem', color: '#555' },
  sendBtn: { background: '#0C2D48', color: '#fff', border: 'none', borderRadius: 8, padding: '0.5rem 1.4rem', cursor: 'pointer', fontFamily: 'inherit', fontWeight: 700, fontSize: '0.85rem' },
};

function AideyDraftCard({ email, onSend, onDismiss, sending }) {
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [body, setBody] = useState(email.draftBody || '');
  const to = email.fromEmail || email.from || '';
  const subject = email.draftSubject || (email.subject?.startsWith('Re:') ? email.subject : `Re: ${email.subject || ''}`);
  const textareaRef = useRef(null);

  const handleEdit = () => {
    setEditing(true);
    setTimeout(() => textareaRef.current?.focus(), 40);
  };

  return (
    <div style={styles.aideyDraftOuter}>
      <button
        onClick={() => setExpanded(e => !e)}
        style={styles.aideyDraftBar}
        aria-expanded={expanded}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <span style={styles.aideySparkle}>✦</span>
          <span style={{ fontWeight: 700, fontSize: '0.82rem', color: '#0C2D48' }}>Aidey drafted a reply</span>
        </span>
        <span style={{ fontSize: '0.75rem', color: '#6b7aad', flexShrink: 0 }}>
          {expanded ? '▲ Hide' : 'Review ▼'}
        </span>
      </button>

      {expanded && (
        <div style={styles.aideyDraftExpanded}>
          <div style={{ fontSize: '0.75rem', color: '#888', marginBottom: '0.5rem' }}>
            <span style={{ color: '#0C2D48', fontWeight: 600 }}>To:</span> {to}
            {subject && <><span style={{ margin: '0 0.35rem', color: '#ccc' }}>·</span><span style={{ color: '#0C2D48', fontWeight: 600 }}>Subject:</span> {subject}</>}
          </div>
          <textarea
            ref={textareaRef}
            value={body}
            onChange={e => setBody(e.target.value)}
            readOnly={!editing}
            rows={6}
            style={{
              ...styles.aideyDraftTextarea,
              background: editing ? '#fff' : '#f8f9fe',
              cursor: editing ? 'text' : 'default',
            }}
          />
          <div style={styles.aideyDraftActions}>
            <button
              onClick={() => onSend({ to, subject, body, email })}
              disabled={sending || !body.trim()}
              style={{ ...styles.aideyDraftSendBtn, opacity: (sending || !body.trim()) ? 0.6 : 1 }}
            >
              {sending ? 'Sending…' : 'Send'}
            </button>
            {!editing && (
              <button onClick={handleEdit} style={styles.aideyDraftEditBtn}>
                Edit &amp; send
              </button>
            )}
            <button onClick={onDismiss} style={styles.aideyDraftDismissBtn}>
              Dismiss
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Aidey Draft Loading Nudge ─────────────────────────────────────────────────

function AideyDraftPending() {
  return (
    <div style={styles.aideyDraftPending}>
      <span style={styles.aideySparkle}>✦</span>
      <span>Aidey is preparing a draft reply…</span>
    </div>
  );
}

// ── Email List Item ───────────────────────────────────────────────────────────

function EmailRow({ email, selected, onSelect, onStar, checked, onToggle, onDelete, onRestore }) {
  return (
    <div
      onClick={() => onSelect(email)}
      style={{
        ...styles.emailRow,
        background: checked ? '#eef2ff' : selected ? '#f0f4ff' : email.isRead ? '#fafafa' : '#fff',
        borderLeft: checked ? '3px solid #4f6ef7' : selected ? '3px solid #0C2D48' : '3px solid transparent',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.4rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', minWidth: 0, flex: 1 }}>
          <input
            type="checkbox"
            checked={checked}
            onChange={onToggle}
            onClick={e => e.stopPropagation()}
            style={{ cursor: 'pointer', flexShrink: 0, accentColor: '#0C2D48', width: 14, height: 14 }}
          />
          {!email.isRead && <span style={styles.unreadDot} />}
          <span style={{ fontWeight: email.isRead ? 400 : 700, fontSize: '0.86rem', color: '#0C2D48', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {email.fromName || email.fromEmail || email.from || 'Unknown'}
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', flexShrink: 0 }}>
          <span style={{ fontSize: '0.72rem', color: '#aaa', whiteSpace: 'nowrap' }}>{formatDate(email.date)}</span>
          <button
            onClick={e => { e.stopPropagation(); onStar(email); }}
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.85rem', padding: '0 2px', color: email.isStarred ? '#E4A010' : '#ddd' }}
            title={email.isStarred ? 'Unstar' : 'Star'}
          >
            {email.isStarred ? '★' : '☆'}
          </button>
          {onRestore ? (
            <button
              onClick={e => { e.stopPropagation(); onRestore([email]); }}
              style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.85rem', padding: '0 2px', color: '#27ae60', lineHeight: 1 }}
              title="Restore to Inbox"
            >
              ↩︎
            </button>
          ) : (
            <button
              onClick={e => { e.stopPropagation(); onDelete([email]); }}
              style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.8rem', padding: '0 2px', color: '#ddd', lineHeight: 1 }}
              title="Delete"
            >
              🗑
            </button>
          )}
          {email.triageCategory && (() => {
            const meta = TRIAGE_META.find(m => m.key === email.triageCategory);
            return meta ? (
              <span style={{ fontSize: '0.6rem', fontWeight: 700, borderRadius: 4, padding: '1px 4px', background: meta.bg, color: meta.color, letterSpacing: '0.01em', whiteSpace: 'nowrap' }}>
                {meta.label}
              </span>
            ) : null;
          })()}
          {email.draftBody && (
            <span style={{ fontSize: '0.6rem', fontWeight: 700, borderRadius: 4, padding: '1px 4px', background: '#eef2ff', color: '#4f6ef7', letterSpacing: '0.01em', whiteSpace: 'nowrap' }}>
              ✦ Draft
            </span>
          )}
          <span style={{ fontSize: '0.65rem', color: '#bbb', background: '#f0f0f0', borderRadius: 4, padding: '0 4px' }}>
            {email.provider}
          </span>
        </div>
      </div>
      <div style={{ fontSize: '0.82rem', fontWeight: email.isRead ? 400 : 600, color: '#222', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginTop: '0.15rem' }}>
        {email.subject}
      </div>
      <div style={{ fontSize: '0.76rem', color: '#888', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginTop: '0.1rem' }}>
        {email.preview}
      </div>
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────

export default function EmailInbox({ setFilter, onAskAidey }) {
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [connections, setConnections] = useState([]);
  const [connectionChecked, setConnectionChecked] = useState(false);

  const [selected, setSelected] = useState(null);
  const [activeFolder, setActiveFolder] = useState('inbox');
  const [activeProvider, setActiveProvider] = useState(null);
  const [activeConnectionId, setActiveConnectionId] = useState(null);
  const [senderFilter, setSenderFilter] = useState(null);

  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const searchDebounceRef = useRef(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState(null);
  const emailListRef = useRef(null);
  const savedScrollTop = useRef(0);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);
  const [mobileDetailOpen, setMobileDetailOpen] = useState(false);

  const [triageFilter, setTriageFilter] = useState(null);

  const [showReply, setShowReply] = useState(false);
  const [replying, setReplying] = useState(false);
  const [showCompose, setShowCompose] = useState(false);
  const [composing, setComposing] = useState(false);
  const [toast, setToast] = useState(null);
  const [emailSuggestion, setEmailSuggestion] = useState(null); // single active email trust prompt
  const [showFolders, setShowFolders] = useState(false);
  const [suggestionActioning, setSuggestionActioning] = useState(false);

  const [selectedIds, setSelectedIds] = useState(new Set());
  const [pendingDeleteItems, setPendingDeleteItems] = useState(null);
  const deleteTimeoutRef = useRef(null);
  const pendingDeleteRef = useRef(new Set());
  const restoreHandlesRef = useRef([]);   // restore-from-trash handles for the current undo window
  const [folders, setFolders] = useState([]);
  const [activeFolderId, setActiveFolderId] = useState(null);
  const [folderAssignments, setFolderAssignments] = useState(null);
  const [showNewFolder, setShowNewFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');

  const [replyInitialBody, setReplyInitialBody] = useState('');
  const [replyInitialSubject, setReplyInitialSubject] = useState('');
  const [dismissedDrafts, setDismissedDrafts] = useState(new Set()); // emailIds dismissed by user
  const [aideyDrafts, setAideyDrafts] = useState([]);
  const [automationConfigs, setAutomationConfigs] = useState({});
  const [senderAutoPopup, setSenderAutoPopup] = useState(null); // emailId with open popup
  const [spamEmails, setSpamEmails] = useState([]);
  const [spamCount, setSpamCount] = useState(0);
  const [failedConnections, setFailedConnections] = useState([]);

  const iframeRef = useRef(null);
  const pollRef = useRef(null);
  const activeProviderRef = useRef(null);
  const activeConnectionIdRef = useRef(null);
  const currentPageRef = useRef(1);
  const searchQueryRef = useRef('');
  const activeFolderRef = useRef('inbox');

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)');
    const handler = e => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  // ── Data fetching ──────────────────────────────────────────────────────────

  const fetchConnections = useCallback(async () => {
    try {
      const res = await api('/api/connections/email');
      const data = await res.json();
      setConnections(data.connections || []);
    } catch {
      setConnections([]);
    } finally {
      setConnectionChecked(true);
    }
  }, []);

  const fetchEmails = useCallback(async (page = 1, q = '', provider = null, folder = null, connectionId = null) => {
    const resolvedFolder = folder !== null ? folder : (activeFolderRef.current || 'inbox');
    let apiPath = '/api/email/inbox';
    if (resolvedFolder === 'sent') apiPath = '/api/email/sent';
    else if (resolvedFolder === 'drafts') apiPath = '/api/email/drafts';
    else if (resolvedFolder === 'deleted') apiPath = '/api/email/deleted';

    setLoading(true);
    setError(null);
    try {
      const emailLimit = window.innerWidth <= 768 ? 5 : 8;
      const params = new URLSearchParams({ page: String(page), limit: String(emailLimit) });
      if (q) params.set('q', q);
      if (provider) params.set('provider', provider);
      const resolvedConnectionId = connectionId !== null ? connectionId : activeConnectionIdRef.current;
      if (resolvedConnectionId) params.set('connectionId', String(resolvedConnectionId));
      const res = await api(`${apiPath}?${params}`);
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Failed to load');
      const data = await res.json();
      const fetched = data.emails || [];
      const pending = pendingDeleteRef.current;
      setEmails(pending.size ? fetched.filter(e => !pending.has(`${e.provider}-${e.id}`)) : fetched);
      setPagination(data.pagination || null);
      setCurrentPage(page);
      setFailedConnections(data.failedConnections || []);
      if (data.totalUnread != null) {
        window.dispatchEvent(new CustomEvent('aidey:emailUnreadCount', { detail: { count: data.totalUnread } }));
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchEmailSuggestion = useCallback(async () => {
    try {
      const res = await api('/api/aidey-intelligence/email-suggestions');
      if (res.ok) {
        const data = await res.json();
        setEmailSuggestion(data.items?.[0] || null);
      }
    } catch { /* non-critical */ }
  }, []);

  const fetchAideyDrafts = useCallback(async () => {
    try {
      const res = await api('/api/email/aidey-drafts');
      if (res.ok) {
        const data = await res.json();
        setAideyDrafts(data.emails || []);
      }
    } catch { /* non-critical */ }
  }, []);

  const fetchSpamEmails = useCallback(async () => {
    try {
      const res = await api('/api/email/spam');
      if (res.ok) {
        const data = await res.json();
        const emails = data.emails || [];
        setSpamEmails(emails);
        setSpamCount(emails.filter(e => !e.spamActioned).length);
      }
    } catch { /* non-critical */ }
  }, []);

  useEffect(() => {
    fetchConnections();
    api('/api/email/automation/config').then(r => r.ok ? r.json() : null).then(d => {
      if (d?.configs) {
        const byIntent = Object.fromEntries(d.configs.map(c => [c.intent_type, c]));
        setAutomationConfigs(byIntent);
      }
    }).catch(() => {});
  }, [fetchConnections]);

  useEffect(() => {
    if (connections.length > 0) {
      fetchEmails();
      fetchEmailSuggestion();
    }
  }, [connections, fetchEmails, fetchEmailSuggestion]);

  // Keep refs in sync so the poll closure always reads current state
  useEffect(() => { activeProviderRef.current = activeProvider; }, [activeProvider]);
  useEffect(() => { activeConnectionIdRef.current = activeConnectionId; }, [activeConnectionId]);
  useEffect(() => { currentPageRef.current = currentPage; }, [currentPage]);
  useEffect(() => { searchQueryRef.current = searchQuery; }, [searchQuery]);
  useEffect(() => { activeFolderRef.current = activeFolder; }, [activeFolder]);

  // Poll every 2 minutes — uses refs so it respects current provider/page/search/folder/connectionId
  useEffect(() => {
    const interval = setInterval(() => {
      fetchEmails(currentPageRef.current, searchQueryRef.current, activeProviderRef.current, activeFolderRef.current, activeConnectionIdRef.current);
      if (activeFolderRef.current === 'drafts') fetchAideyDrafts();
    }, 2 * 60 * 1000);
    pollRef.current = interval;
    return () => clearInterval(interval);
  }, [fetchEmails, fetchAideyDrafts]);

  const fetchFolders = useCallback(async () => {
    try {
      const res = await api('/api/email/folders');
      const data = await res.json();
      setFolders(data.folders || []);
    } catch { setFolders([]); }
  }, []);

  useEffect(() => { fetchFolders(); }, [fetchFolders]);

  useEffect(() => {
    if (!activeFolderId) { setFolderAssignments(null); return; }
    api(`/api/email/folders/${activeFolderId}/assignments`)
      .then(r => r.json())
      .then(d => setFolderAssignments(d.assignments || []))
      .catch(() => setFolderAssignments([]));
  }, [activeFolderId]);

  // ── Selection + mark read ──────────────────────────────────────────────────

  const handleSelect = useCallback(async (email) => {
    if (window.innerWidth <= 768 && emailListRef.current) {
      savedScrollTop.current = emailListRef.current.scrollTop;
      setMobileDetailOpen(true);
    }
    setSelected(email);
    setShowReply(false);
    if (!email.isRead) {
      try {
        const res = await api(`/api/email/inbox/${email.id}/read`, {
          method: 'POST',
          body: JSON.stringify({ provider: email.provider }),
        });
        if (res.ok) {
          setEmails(prev => prev.map(e => e.id === email.id ? { ...e, isRead: true } : e));
          setSelected(prev => prev?.id === email.id ? { ...prev, isRead: true } : prev);
        } else if (res.status === 403) {
          showToast('Gmail permissions need updating — please reconnect Gmail in Settings > Email connections', 'error');
        }
      } catch { /* non-critical */ }
    }
  }, []);

  const handleMobileBack = useCallback(() => {
    setMobileDetailOpen(false);
    setTimeout(() => {
      setSelected(null);
      if (emailListRef.current) {
        emailListRef.current.scrollTop = savedScrollTop.current;
      }
    }, 230);
  }, []);

  // ── Star ───────────────────────────────────────────────────────────────────

  const handleStar = useCallback(async (email) => {
    const newStarred = !email.isStarred;
    setEmails(prev => prev.map(e => e.id === email.id ? { ...e, isStarred: newStarred } : e));
    if (selected?.id === email.id) setSelected(prev => ({ ...prev, isStarred: newStarred }));
    try {
      await api(`/api/email/inbox/${email.id}/star`, {
        method: 'POST',
        body: JSON.stringify({ provider: email.provider, starred: newStarred }),
      });
    } catch {
      setEmails(prev => prev.map(e => e.id === email.id ? { ...e, isStarred: email.isStarred } : e));
    }
  }, [selected]);

  // ── Send reply ─────────────────────────────────────────────────────────────

  const handleSend = useCallback(async ({ to, subject, body, email }) => {
    setReplying(true);
    try {
      const res = await api('/api/email/reply', {
        method: 'POST',
        body: JSON.stringify({
          messageId: email.id,
          threadId: email.threadId,
          provider: email.provider,
          connectionId: email.connectionId || null,
          to,
          subject,
          body: body.replace(/\n/g, '<br>'),
        }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Send failed');
      setShowReply(false);
      showToast('Reply sent');
      // Check if this reply pushed the pattern over the threshold
      setTimeout(fetchEmailSuggestion, 1500);
    } catch (err) {
      showToast('Failed to send: ' + err.message, 'error');
    } finally {
      setReplying(false);
    }
  }, [fetchEmailSuggestion]);

  // ── Toast ──────────────────────────────────────────────────────────────────

  const showToast = useCallback((msg, type = 'success', showUndo = false) => {
    setToast({ msg, type, showUndo });
    if (!showUndo) setTimeout(() => setToast(null), 3000);
  }, []);

  // Compose a brand-new email. Routes through the same /api/email/reply endpoint
  // as replies (messageId/threadId null = a fresh email); the From selector maps
  // to a specific connection via provider + connectionId — no forked send logic.
  const handleCompose = useCallback(async ({ connection, to, cc, bcc, subject, body }) => {
    setComposing(true);
    try {
      const res = await api('/api/email/reply', {
        method: 'POST',
        body: JSON.stringify({
          messageId: null,
          threadId: null,
          provider: connection.provider,
          connectionId: connection.id,
          to,
          cc: cc || undefined,
          bcc: bcc || undefined,
          subject,
          body: body.replace(/\n/g, '<br>'),
        }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Send failed');
      setShowCompose(false);
      showToast('Email sent');
      if (activeFolderRef.current === 'sent') fetchEmails(1, '', activeProviderRef.current, 'sent');
    } catch (err) {
      showToast('Failed to send: ' + err.message, 'error');
    } finally {
      setComposing(false);
    }
  }, [showToast, fetchEmails]);

  // ── Multi-select ──────────────────────────────────────────────────────────

  const handleToggleSelect = useCallback((emailKey) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(emailKey)) next.delete(emailKey); else next.add(emailKey);
      return next;
    });
  }, []);

  // ── Delete with undo ──────────────────────────────────────────────────────

  // Undo = restore-from-trash. The delete has ALREADY been committed on the provider;
  // undo re-inserts the emails locally and moves them back out of trash server-side.
  const handleUndo = useCallback(() => {
    clearTimeout(deleteTimeoutRef.current);
    pendingDeleteRef.current = new Set();
    const snapshot = pendingDeleteItems;
    const handles = restoreHandlesRef.current;
    setPendingDeleteItems(null);
    setToast(null);
    if (snapshot) {
      // Re-insert visually straight away.
      setEmails(cur => {
        const existing = new Set(cur.map(e => `${e.provider}-${e.id}`));
        const toAdd = snapshot.filter(e => !existing.has(`${e.provider}-${e.id}`));
        return [...toAdd, ...cur].sort((a, b) => new Date(b.date) - new Date(a.date));
      });
      // Move them back out of trash on the provider (only for handles we captured).
      if (handles && handles.length) {
        api('/api/email/inbox/restore', {
          method: 'POST',
          body: JSON.stringify({ items: handles }),
        }).catch(() => {});
      }
    }
    restoreHandlesRef.current = [];
  }, [pendingDeleteItems]);

  // Restore trashed mail back to the Inbox (from the Deleted view). Reuses the
  // same /inbox/restore endpoint as the delete-undo, with handles built from the
  // deleted email (imap needs trashPath+uid, which the /deleted list carries).
  const handleRestore = useCallback(async (emailsToRestore) => {
    if (!emailsToRestore.length) return;
    const handles = emailsToRestore.map(e =>
      e.provider === 'imap'
        ? { originalId: e.id, provider: 'imap', connectionId: e.connectionId, trashPath: e.mailboxPath, uid: e.uid }
        : { originalId: e.id, provider: e.provider, connectionId: e.connectionId, id: e.id }
    );
    const keys = new Set(emailsToRestore.map(e => `${e.provider}-${e.id}`));
    setEmails(prev => prev.filter(e => !keys.has(`${e.provider}-${e.id}`)));
    if (selected && keys.has(`${selected.provider}-${selected.id}`)) setSelected(null);
    try {
      const res = await api('/api/email/inbox/restore', { method: 'POST', body: JSON.stringify({ items: handles }) });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Restore failed');
      showToast(`Restored ${data.restored || emailsToRestore.length} to Inbox`);
    } catch (err) {
      showToast('Failed to restore: ' + err.message, 'error');
      fetchEmails(1, '', activeProviderRef.current, 'deleted'); // resync on failure
    }
  }, [showToast, fetchEmails, selected]);

  // Permanently empty ONE account's trash (the account currently in view).
  // Gmail is never offered (scope-limited); "All accounts" never empties everything.
  const handleEmptyTrash = useCallback(async () => {
    let body = null;
    if (activeProvider === 'outlook') body = { provider: 'outlook' };
    else if (activeProvider === 'imap' && activeConnectionId) body = { provider: 'imap', connectionId: activeConnectionId };
    if (!body) return;

    const count = pagination?.total ?? emails.length;
    if (!window.confirm(`Permanently delete ${count} email${count === 1 ? '' : 's'} from Trash? This can't be undone.`)) return;

    try {
      const res = await api('/api/email/deleted/empty', { method: 'POST', body: JSON.stringify(body) });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || data.error || 'Failed to empty trash');
      const n = data.deleted ?? count;
      showToast(`Permanently deleted ${n} email${n === 1 ? '' : 's'}`);
      setEmails([]);
      setSelected(null);
      fetchEmails(1, '', activeProviderRef.current, 'deleted');
    } catch (err) {
      showToast('Failed to empty trash: ' + err.message, 'error');
    }
  }, [activeProvider, activeConnectionId, pagination, emails.length, showToast, fetchEmails]);

  const handleDelete = useCallback(async (emailsToDelete) => {
    if (!emailsToDelete.length) return;
    // Clear any prior undo window (its delete was already committed).
    clearTimeout(deleteTimeoutRef.current);
    // Optimistic remove + client-side tombstone so a refresh mid-request can't re-add it.
    const keys = new Set(emailsToDelete.map(e => `${e.provider}-${e.id}`));
    pendingDeleteRef.current = keys;
    setEmails(prev => prev.filter(e => !keys.has(`${e.provider}-${e.id}`)));
    setSelectedIds(new Set());
    setPendingDeleteItems(emailsToDelete);
    const label = emailsToDelete.length === 1 ? 'Email' : `${emailsToDelete.length} emails`;
    showToast(`${label} deleted`, 'success', true);

    // Commit the provider delete IMMEDIATELY (no deferred timer).
    try {
      const res = await api('/api/email/inbox/bulk-delete', {
        method: 'POST',
        body: JSON.stringify({ items: emailsToDelete.map(e => ({ id: e.id, provider: e.provider })) }),
      });
      const data = res.ok ? await res.json() : null;
      if (!data) throw new Error(`Server returned ${res.status}`);
      restoreHandlesRef.current = Array.isArray(data.restore) ? data.restore : [];

      // Some items may have failed server-side even on HTTP 200 — restore exactly
      // those in local state so they don't silently reappear on the next refresh,
      // and tell the user the truth instead of trusting res.ok alone.
      const failedList = Array.isArray(data.failed) ? data.failed : [];
      if (failedList.length) {
        const failedKeys = new Set(failedList.map(f => `${f.provider}-${f.id}`));
        const toRestore = emailsToDelete.filter(e => failedKeys.has(`${e.provider}-${e.id}`));
        // Don't tombstone the failed ones locally — they're still live on the server.
        pendingDeleteRef.current = new Set(
          [...pendingDeleteRef.current].filter(k => !failedKeys.has(k))
        );
        if (toRestore.length) {
          setEmails(prev => {
            const existing = new Set(prev.map(e => `${e.provider}-${e.id}`));
            const add = toRestore.filter(e => !existing.has(`${e.provider}-${e.id}`));
            return [...add, ...prev].sort((a, b) => new Date(b.date) - new Date(a.date));
          });
        }
        const n = failedList.length;
        showToast(`Couldn't delete ${n} email${n === 1 ? '' : 's'} — connection issue`, 'error');
      }
    } catch (err) {
      // Commit failed — put them back and surface the error.
      restoreHandlesRef.current = [];
      pendingDeleteRef.current = new Set();
      setPendingDeleteItems(null);
      showToast('Delete failed: ' + err.message, 'error');
      setEmails(prev => {
        const existing = new Set(prev.map(e => `${e.provider}-${e.id}`));
        const toRestore = emailsToDelete.filter(e => !existing.has(`${e.provider}-${e.id}`));
        return [...toRestore, ...prev].sort((a, b) => new Date(b.date) - new Date(a.date));
      });
      return;
    }

    // Keep the undo window open for 8s, then just close it (delete already committed).
    deleteTimeoutRef.current = setTimeout(() => {
      pendingDeleteRef.current = new Set();
      restoreHandlesRef.current = [];
      setPendingDeleteItems(null);
      setToast(null);
    }, 8000);
  }, [showToast]);

  // ── Bulk mark read/unread ─────────────────────────────────────────────────

  const handleBulkMarkRead = useCallback(async (markAsRead) => {
    const toMark = emails.filter(e => selectedIds.has(`${e.provider}-${e.id}`));
    if (!toMark.length) return;
    setEmails(prev => prev.map(e => selectedIds.has(`${e.provider}-${e.id}`) ? { ...e, isRead: markAsRead } : e));
    setSelectedIds(new Set());
    try {
      await Promise.all(toMark.map(e =>
        api(`/api/email/inbox/${e.id}/read`, {
          method: 'POST',
          body: JSON.stringify({ provider: e.provider, isRead: markAsRead }),
        })
      ));
    } catch (err) {
      setEmails(prev => prev.map(e => {
        const orig = toMark.find(m => m.id === e.id && m.provider === e.provider);
        return orig ? { ...e, isRead: orig.isRead } : e;
      }));
      showToast('Mark failed: ' + err.message, 'error');
    }
  }, [emails, selectedIds, showToast]);

  // ── Move to folder ────────────────────────────────────────────────────────

  const handleMoveToFolder = useCallback(async (folderId) => {
    const toMove = emails.filter(e => selectedIds.has(`${e.provider}-${e.id}`));
    if (!toMove.length) return;
    try {
      await Promise.all(toMove.map(e =>
        api(`/api/email/folders/${folderId}/assign`, {
          method: 'POST',
          body: JSON.stringify({ emailId: e.id, emailSource: e.provider }),
        })
      ));
      const n = toMove.length;
      showToast(`${n} email${n !== 1 ? 's' : ''} moved to folder`);
      setSelectedIds(new Set());
      if (activeFolderId === folderId) {
        const res = await api(`/api/email/folders/${folderId}/assignments`);
        const d = await res.json();
        setFolderAssignments(d.assignments || []);
      }
    } catch (err) {
      showToast('Move failed: ' + err.message, 'error');
    }
  }, [emails, selectedIds, activeFolderId, showToast]);

  // ── Folder CRUD ───────────────────────────────────────────────────────────

  const handleCreateFolder = useCallback(async () => {
    const name = newFolderName.trim();
    if (!name) return;
    try {
      const res = await api('/api/email/folders', {
        method: 'POST',
        body: JSON.stringify({ name }),
      });
      const data = await res.json();
      if (data.folder) setFolders(prev => [...prev, data.folder].sort((a, b) => a.name.localeCompare(b.name)));
      setShowNewFolder(false);
      setNewFolderName('');
    } catch (err) {
      showToast('Failed to create folder: ' + err.message, 'error');
    }
  }, [newFolderName, showToast]);

  const handleDeleteFolder = useCallback(async (folderId) => {
    setFolders(prev => prev.filter(f => f.id !== folderId));
    if (activeFolderId === folderId) { setActiveFolderId(null); setFolderAssignments(null); }
    try {
      await api(`/api/email/folders/${folderId}`, { method: 'DELETE' });
    } catch (err) {
      showToast('Failed to delete folder: ' + err.message, 'error');
      fetchFolders();
    }
  }, [activeFolderId, showToast, fetchFolders]);

  // ── Inline draft suggestion ───────────────────────────────────────────────

  const handleDismissDraft = useCallback((emailId) => {
    setDismissedDrafts(prev => new Set([...prev, emailId]));
  }, []);

  // ── Ask Aidey ─────────────────────────────────────────────────────────────

  const handleAskAidey = useCallback((email) => {
    const bodyText = stripHtml(email.body || '').slice(0, 200).trim();
    const context = `Help me with this email from ${email.fromName || email.fromEmail}: "${email.subject}".${bodyText ? `\n\n${bodyText}${bodyText.length === 200 ? '…' : ''}` : ''}`;
    if (typeof onAskAidey === 'function') {
      onAskAidey(context);
    } else {
      window.dispatchEvent(new CustomEvent('aidey:openChatWithContext', { detail: { context } }));
    }
  }, [onAskAidey]);

  // ── Write with Aidey ──────────────────────────────────────────────────────

  const handleWriteWithAidey = useCallback(({ email }) => {
    const prompt = `Draft a reply to this email from ${email.fromName || email.fromEmail}.\n\nOriginal subject: ${email.subject}\nOriginal message: ${stripHtml(email.body).slice(0, 400)}`;
    window.dispatchEvent(new CustomEvent('aidey:openChatWithContext', { detail: { context: prompt } }));
    if (typeof setFilter === 'function') setFilter('AideyChat');
  }, [setFilter]);

  // ── Email trust prompt actions ─────────────────────────────────────────────

  const handleAcceptEmailTrust = useCallback(async () => {
    if (!emailSuggestion) return;
    setSuggestionActioning(true);
    try {
      const res = await api(`/api/aidey-intelligence/email-suggestions/${emailSuggestion.id}/accept`, { method: 'POST' });
      if (res.ok) {
        setEmailSuggestion(null);
        showToast('Aidey will now handle similar replies automatically — you\'ll be notified each time');
      }
    } catch { /* non-critical */ }
    setSuggestionActioning(false);
  }, [emailSuggestion]);

  const handleDismissEmailTrust = useCallback(async () => {
    if (!emailSuggestion) return;
    setSuggestionActioning(true);
    try {
      await api(`/api/aidey-intelligence/email-suggestions/${emailSuggestion.id}`, { method: 'DELETE' });
    } catch { /* non-critical */ }
    setEmailSuggestion(null);
    setSuggestionActioning(false);
  }, [emailSuggestion]);

  // ── Add to CRM ────────────────────────────────────────────────────────────

  const handleAddToCrm = useCallback(async (email) => {
    try {
      const res = await api('/api/contacts', {
        method: 'POST',
        body: JSON.stringify({ name: email.fromName || email.fromEmail, email: email.fromEmail }),
      });
      if (!res.ok) throw new Error('Failed to add contact');
      showToast(`${email.fromName || email.fromEmail} added to CRM`);
      window.dispatchEvent(new Event('aidey:contactsUpdated'));
    } catch (err) {
      showToast('Failed to add to CRM: ' + err.message, 'error');
    }
  }, []);

  // ── Search input handler ───────────────────────────────────────────────────

  function handleSearchChange(val) {
    setSearchInput(val);
    clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      const q = val.trim().toLowerCase();
      setSearchQuery(q);
      fetchEmails(1, q, activeProvider);
      if (emailListRef.current) emailListRef.current.scrollTop = 0;
    }, 300);
  }

  function clearSearch() {
    setSearchInput('');
    setSearchQuery('');
    clearTimeout(searchDebounceRef.current);
    fetchEmails(1, '', activeProvider);
    if (emailListRef.current) emailListRef.current.scrollTop = 0;
  }

  // ── Derived email list ─────────────────────────────────────────────────────

  const providers = [...new Set(emails.map(e => e.provider))];

  let displayEmails = emails;
  if (activeFolder === 'starred') displayEmails = displayEmails.filter(e => e.isStarred);
  if (senderFilter) displayEmails = displayEmails.filter(e => e.fromEmail?.toLowerCase().includes(senderFilter.toLowerCase()) || e.fromName?.toLowerCase().includes(senderFilter.toLowerCase()));
  if (triageFilter) {
    displayEmails = displayEmails.filter(e => e.triageCategory === triageFilter);
  }

  if (activeFolderId && folderAssignments) {
    const assignedIds = new Set(folderAssignments.map(a => a.emailId));
    displayEmails = displayEmails.filter(e => assignedIds.has(e.id));
  }

  const unreadCount = emails.filter(e => !e.isRead).length;

  // ── Inline handlers that need displayEmails ────────────────────────────────
  const allSelected = displayEmails.length > 0 && displayEmails.every(e => selectedIds.has(`${e.provider}-${e.id}`));
  const handleSelectAll = (ev) => {
    if (ev.target.checked) setSelectedIds(new Set(displayEmails.map(e => `${e.provider}-${e.id}`)));
    else setSelectedIds(new Set());
  };
  const selectedEmailsList = emails.filter(e => selectedIds.has(`${e.provider}-${e.id}`));

  // ── Empty states ───────────────────────────────────────────────────────────

  if (!connectionChecked) {
    return (
      <div style={styles.root}>
        <div style={styles.centerState}><div style={styles.spinner} /><p>Checking connection…</p></div>
      </div>
    );
  }

  const activeConnections = connections.filter(c => ['gmail', 'outlook', 'imap'].includes(c.provider));

  if (activeConnections.length === 0) {
    return (
      <div style={styles.root}>
        <div style={styles.centerState}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>✉️</div>
          <h2 style={{ margin: '0 0 0.5rem', color: '#0C2D48', fontFamily: 'Poppins, sans-serif' }}>Connect your email</h2>
          <p style={{ color: '#555', margin: '0 0 1.5rem', maxWidth: 380, textAlign: 'center', lineHeight: 1.6 }}>
            Connect Gmail, Outlook, or another email provider to view and manage your inbox directly in Aidey.
          </p>
          <button
            onClick={() => typeof setFilter === 'function' && setFilter('Settings')}
            style={styles.connectBtn}
          >
            Connect Email Account
          </button>
        </div>
      </div>
    );
  }

  // ── Main render ────────────────────────────────────────────────────────────

  return (
    <div className="email-inbox-fill" style={styles.root}>
      {/* Triage filter bar — auto-populated from email categories */}
      <TriageBar emails={emails} triageFilter={triageFilter} onFilter={setTriageFilter} />

      {/* ── Per-connection error banners — failed fetches (e.g. expired token) ── */}
      {failedConnections.map(fc => (
        <div key={fc.connectionId} style={{
          display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap',
          margin: '0.5rem 0.75rem 0', padding: '0.6rem 0.9rem',
          background: '#fff8ee', border: '1px solid #f0c070', borderRadius: '8px',
          fontSize: '0.83rem', color: '#7a4a10',
        }}>
          <span>⚠️</span>
          <span style={{ flex: 1 }}>
            Could not load {fc.provider === 'gmail' ? 'Gmail' : fc.provider === 'outlook' ? 'Outlook' : fc.email} ({fc.email}). Your connection may have expired.
          </span>
          <button
            onClick={() => typeof setFilter === 'function' && setFilter('Settings')}
            style={{
              background: 'none', border: '1px solid #c07a20', borderRadius: '6px',
              padding: '0.3rem 0.65rem', fontSize: '0.8rem', color: '#7a4a10',
              cursor: 'pointer', fontFamily: "'Raleway', sans-serif", fontWeight: 600,
              whiteSpace: 'nowrap',
            }}
          >
            Reconnect in Settings
          </button>
        </div>
      ))}

      {/* ── Email trust prompt (shown when intelligence threshold met) ── */}
      {emailSuggestion && (
        <div style={styles.trustPrompt}>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: '0.84rem', color: '#0C2D48', marginBottom: '0.25rem' }}>
              Aidey has noticed a pattern
            </div>
            <div style={{ fontSize: '0.8rem', color: '#444', lineHeight: 1.5 }}>
              You've sent similar replies to <strong>{emailSuggestion.contactEmail}</strong> {emailSuggestion.occurrences} times.
              Would you like Aidey to handle emails like this automatically from now on, and notify you each time it does?
            </div>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0, alignItems: 'center', flexWrap: 'wrap' }}>
            <button
              onClick={handleAcceptEmailTrust}
              disabled={suggestionActioning}
              style={styles.trustAcceptBtn}
            >
              Yes, handle automatically
            </button>
            <button
              onClick={handleDismissEmailTrust}
              disabled={suggestionActioning}
              style={styles.trustDeclineBtn}
            >
              No, keep asking me
            </button>
            <button
              onClick={() => setEmailSuggestion(null)}
              style={styles.trustLaterBtn}
            >
              Remind me later
            </button>
          </div>
        </div>
      )}

      {/* Mobile folders toggle — only visible when stacked (CSS hides on desktop) */}
      <button
        className="email-folders-toggle"
        onClick={() => setShowFolders(v => !v)}
        style={{ display: 'none' }}
      >
        {showFolders ? '✕ Hide Folders' : '☰ Folders'}
      </button>

      <div className="email-content" style={styles.panels}>
        {/* ── Left panel: folders ── */}
        <div className={`email-folder-panel${showFolders ? '' : ' email-folder-panel--hidden'}`} style={styles.leftPanel}>
          <div style={styles.leftHeader}>
            <span style={{ fontWeight: 700, fontSize: '0.88rem', color: '#0C2D48' }}>Folders</span>
            <button onClick={() => fetchEmails(1, searchQuery, activeProvider)} style={styles.refreshBtn} title="Refresh">↻</button>
          </div>

          <button
            onClick={() => setShowCompose(true)}
            disabled={activeConnections.length === 0}
            style={styles.composeBtn}
            title={activeConnections.length === 0 ? 'Connect an email account first' : 'New email'}
          >
            ✏️ New email
          </button>

          <button
            onClick={() => { setActiveFolder('inbox'); setSenderFilter(null); setTriageFilter(null); setActiveFolderId(null); }}
            style={{ ...styles.folderBtn, ...(activeFolder === 'inbox' && !senderFilter && !triageFilter && !activeFolderId ? styles.folderActive : {}) }}
          >
            <span>📥 Inbox</span>
            {unreadCount > 0 && <span style={styles.unreadBadge}>{unreadCount}</span>}
          </button>
          <button
            onClick={() => { setActiveFolder('starred'); setSenderFilter(null); setTriageFilter(null); setActiveFolderId(null); }}
            style={{ ...styles.folderBtn, ...(activeFolder === 'starred' && !activeFolderId ? styles.folderActive : {}) }}
          >
            ★ Starred
          </button>
          <button
            onClick={() => { setActiveFolder('sent'); setSenderFilter(null); setTriageFilter(null); setActiveFolderId(null); fetchEmails(1, '', activeProvider, 'sent'); }}
            style={{ ...styles.folderBtn, ...(activeFolder === 'sent' ? styles.folderActive : {}) }}
          >
            📤 Sent
          </button>
          <button
            onClick={() => { setActiveFolder('drafts'); setSenderFilter(null); setTriageFilter(null); setActiveFolderId(null); fetchEmails(1, '', activeProvider, 'drafts'); fetchAideyDrafts(); }}
            style={{ ...styles.folderBtn, ...(activeFolder === 'drafts' ? styles.folderActive : {}) }}
          >
            <span>📝 Drafts</span>
            {aideyDrafts.length > 0 && <span style={styles.unreadBadge}>{aideyDrafts.length}</span>}
          </button>
          <button
            onClick={() => { setActiveFolder('spam'); setSenderFilter(null); setTriageFilter(null); setActiveFolderId(null); fetchSpamEmails(); }}
            style={{ ...styles.folderBtn, ...(activeFolder === 'spam' ? styles.folderActive : {}) }}
          >
            <span>🚫 Spam</span>
            {spamCount > 0 && <span style={styles.unreadBadge}>{spamCount}</span>}
          </button>
          <button
            onClick={() => { setActiveFolder('deleted'); setSenderFilter(null); setTriageFilter(null); setActiveFolderId(null); fetchEmails(1, '', activeProvider, 'deleted'); }}
            style={{ ...styles.folderBtn, ...(activeFolder === 'deleted' ? styles.folderActive : {}) }}
          >
            🗑 Deleted
          </button>

          {activeConnections.length > 1 && (
            <>
              <div style={styles.sectionLabel}>Accounts</div>
              <button
                onClick={() => { setActiveProvider(null); setActiveConnectionId(null); setActiveFolderId(null); fetchEmails(1, searchQuery, null, null, null); }}
                style={{ ...styles.folderBtn, ...(!activeProvider && !activeConnectionId ? styles.folderActive : {}) }}
              >
                All accounts
              </button>
              {activeConnections.map(conn => {
                if (conn.provider !== 'imap') {
                  return (
                    <button
                      key={conn.id}
                      onClick={() => { setActiveProvider(conn.provider); setActiveConnectionId(null); setActiveFolderId(null); fetchEmails(1, searchQuery, conn.provider, null, null); }}
                      style={{ ...styles.folderBtn, ...(activeProvider === conn.provider && !activeConnectionId ? styles.folderActive : {}) }}
                    >
                      {conn.provider === 'gmail' ? '📧 Gmail' : '📨 Outlook'}
                    </button>
                  );
                }
                // IMAP: show per-connection entry using connectionId
                const label = conn.label || conn.email || 'IMAP';
                return (
                  <button
                    key={conn.id}
                    onClick={() => { setActiveProvider('imap'); setActiveConnectionId(conn.id); setActiveFolderId(null); fetchEmails(1, searchQuery, 'imap', null, conn.id); }}
                    style={{ ...styles.folderBtn, ...(activeConnectionId === conn.id ? styles.folderActive : {}) }}
                  >
                    ✉️ {label}
                  </button>
                );
              })}
            </>
          )}

          {/* ── Aidey Folders (organisational labels, Aidey-side only) ── */}
          <div style={{ ...styles.sectionLabel, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>Aidey Folders</span>
          </div>
          {folders.map(f => (
            <div key={f.id} style={{ display: 'flex', alignItems: 'center' }}>
              <button
                onClick={() => { setActiveFolderId(f.id); setActiveFolder('inbox'); setSenderFilter(null); setTriageFilter(null); }}
                style={{ ...styles.folderBtn, flex: 1, ...(activeFolderId === f.id ? styles.folderActive : {}) }}
              >
                📁 {f.name}
              </button>
              <button
                onClick={() => handleDeleteFolder(f.id)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ccc', fontSize: '0.7rem', padding: '0 0.5rem', flexShrink: 0, lineHeight: 1 }}
                title="Delete folder"
              >
                ✕
              </button>
            </div>
          ))}
          {showNewFolder ? (
            <div style={{ padding: '0.25rem 0.6rem', display: 'flex', gap: '0.25rem' }}>
              <input
                autoFocus
                value={newFolderName}
                onChange={e => setNewFolderName(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') handleCreateFolder(); if (e.key === 'Escape') { setShowNewFolder(false); setNewFolderName(''); } }}
                placeholder="Folder name"
                style={{ flex: 1, fontSize: '0.8rem', border: '1px solid #ddd', borderRadius: 4, padding: '0.2rem 0.4rem', fontFamily: "'Raleway', sans-serif", outline: 'none', minWidth: 0 }}
              />
              <button onClick={handleCreateFolder} style={{ background: '#0C2D48', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: '0.75rem', padding: '0 0.5rem', flexShrink: 0 }}>✓</button>
              <button onClick={() => { setShowNewFolder(false); setNewFolderName(''); }} style={{ background: 'none', border: '1px solid #ddd', borderRadius: 4, cursor: 'pointer', fontSize: '0.75rem', padding: '0 0.4rem', color: '#888', flexShrink: 0 }}>✕</button>
            </div>
          ) : (
            <button
              onClick={() => setShowNewFolder(true)}
              style={{ ...styles.folderBtn, color: '#aaa', fontSize: '0.8rem' }}
            >
              + New folder
            </button>
          )}
        </div>

        {/* ── Middle panel: email list ── */}
        <div className="email-list" style={styles.middlePanel}>
          {/* Search bar */}
          <div style={styles.searchBar}>
            <span style={styles.searchIcon}>&#x2315;</span>
            <input
              type="search"
              placeholder="Search emails..."
              value={searchInput}
              onChange={e => handleSearchChange(e.target.value)}
              style={styles.searchInput}
              autoComplete="off"
              autoCorrect="off"
              spellCheck={false}
            />
            {searchInput && (
              <button onClick={clearSearch} style={styles.searchClear} aria-label="Clear search">
                &#x2715;
              </button>
            )}
          </div>

          {/* Empty Trash bar — Deleted view only, scoped to the account in view */}
          {activeFolder === 'deleted' && (
            <div style={styles.trashBar}>
              {activeProvider === 'gmail' ? (
                <span style={styles.trashNote}>Gmail empties its own Trash after 30 days — Aidey can't permanently delete Gmail mail.</span>
              ) : (activeProvider === 'outlook' || (activeProvider === 'imap' && activeConnectionId)) ? (
                <button onClick={handleEmptyTrash} style={styles.emptyTrashBtn} disabled={(pagination?.total ?? emails.length) === 0}>
                  🗑 Empty Trash{(pagination?.total ?? 0) > 0 ? ` (${pagination.total})` : ''}
                </button>
              ) : (
                <span style={styles.trashNote}>To permanently empty Trash, pick a single account on the left — Empty Trash works one account at a time.</span>
              )}
            </div>
          )}

          {/* Bulk action bar */}
          {selectedIds.size > 0 && (
            <div style={styles.bulkBar}>
              <span style={{ fontSize: '0.78rem', fontWeight: 700, color: '#0C2D48', flexShrink: 0 }}>{selectedIds.size} selected</span>
              {activeFolder === 'deleted' ? (
                // In Trash, the primary action is Restore. No bulk Delete here:
                // permanent-delete has the same Gmail-scope limit as empty-trash,
                // so it's handled per-account via Empty Trash, not half-working here.
                <button
                  onClick={() => { handleRestore(selectedEmailsList); setSelectedIds(new Set()); }}
                  style={{ ...styles.bulkBtn, color: '#27ae60', fontWeight: 700 }}
                >
                  ↩︎ Restore to Inbox
                </button>
              ) : (
                <>
                  <button onClick={() => handleDelete(selectedEmailsList)} style={styles.bulkBtn}>🗑 Delete</button>
                  <button onClick={() => handleBulkMarkRead(true)} style={styles.bulkBtn}>Mark read</button>
                  <button onClick={() => handleBulkMarkRead(false)} style={styles.bulkBtn}>Mark unread</button>
                  {folders.length > 0 && (
                    <select
                      defaultValue=""
                      onChange={e => { if (e.target.value) { handleMoveToFolder(e.target.value); e.target.value = ''; } }}
                      style={{ fontSize: '0.75rem', border: '1px solid #ddd', borderRadius: 4, padding: '0.15rem 0.3rem', cursor: 'pointer', fontFamily: "'Raleway', sans-serif", background: '#fff', color: '#333' }}
                    >
                      <option value="" disabled>Move to…</option>
                      {folders.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
                    </select>
                  )}
                </>
              )}
              <button onClick={() => setSelectedIds(new Set())} style={{ ...styles.bulkBtn, marginLeft: 'auto', color: '#888' }}>✕ Clear</button>
            </div>
          )}

          <div style={styles.emailRows} ref={emailListRef}>
            {activeFolder === 'spam' ? (
              <>
                <div style={{ padding: '0.6rem 0.9rem', fontSize: '0.78rem', background: '#fff8ee', borderBottom: '1px solid #f0c070', color: '#7a4a10', fontFamily: "'Raleway', sans-serif" }}>
                  Aidey moved these here. Restore anything that does not belong.
                </div>
                {spamEmails.length === 0 ? (
                  <div style={{ padding: '2rem', textAlign: 'center', fontSize: '0.84rem', color: '#aaa', fontFamily: "'Raleway', sans-serif" }}>No spam emails</div>
                ) : spamEmails.map(email => (
                  <div key={email.id} style={{ padding: '0.65rem 0.9rem', borderBottom: '1px solid #eee', display: 'flex', alignItems: 'flex-start', gap: '0.5rem', background: email.spamActioned ? '#fafafa' : '#fff' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.4rem' }}>
                        <span style={{ fontWeight: 600, fontSize: '0.86rem', color: '#333', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, fontFamily: "'Raleway', sans-serif" }}>
                          {email.fromName || email.fromEmail}
                        </span>
                        <span style={{ fontSize: '0.72rem', color: '#aaa', whiteSpace: 'nowrap', flexShrink: 0 }}>{email.date}</span>
                      </div>
                      <div style={{ fontSize: '0.8rem', color: '#555', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontFamily: "'Raleway', sans-serif" }}>{email.subject}</div>
                      <div style={{ fontSize: '0.75rem', color: '#aaa', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontFamily: "'Raleway', sans-serif" }}>{email.preview}</div>
                    </div>
                    <div style={{ display: 'flex', gap: '0.4rem', flexShrink: 0, marginTop: '0.15rem' }}>
                      <button
                        onClick={async () => {
                          try {
                            await api(`/api/email/spam/${email.id}/restore`, { method: 'POST' });
                            setSpamEmails(prev => prev.filter(e => e.id !== email.id));
                            setSpamCount(prev => Math.max(0, prev - (email.spamActioned ? 0 : 1)));
                          } catch { /* non-critical */ }
                        }}
                        style={{ fontSize: '0.75rem', padding: '0.28rem 0.6rem', borderRadius: 6, border: '1px solid #27ae60', color: '#27ae60', background: '#fff', cursor: 'pointer', fontFamily: "'Raleway', sans-serif" }}
                      >
                        Not spam
                      </button>
                      {!email.spamActioned && (
                        <button
                          onClick={async () => {
                            try {
                              await api(`/api/email/spam/${email.id}/confirm`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ provider: email.provider }) });
                              setSpamEmails(prev => prev.map(e => e.id === email.id ? { ...e, spamActioned: true } : e));
                              setSpamCount(prev => Math.max(0, prev - 1));
                            } catch { /* non-critical */ }
                          }}
                          style={{ fontSize: '0.75rem', padding: '0.28rem 0.6rem', borderRadius: 6, border: '1px solid #E96A6A', color: '#E96A6A', background: '#fff', cursor: 'pointer', fontFamily: "'Raleway', sans-serif" }}
                        >
                          Confirm spam
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </>
            ) : activeFolder === 'drafts' ? (
              <>
                {/* Aidey Drafts section */}
                <div style={{ padding: '0.35rem 0.9rem', fontSize: '0.72rem', fontWeight: 700, color: '#6b7aad', background: '#f8f9fe', borderBottom: '1px solid #eee' }}>
                  ✦ Aidey Drafts {aideyDrafts.length > 0 ? `(${aideyDrafts.length})` : ''}
                </div>
                {aideyDrafts.length === 0 ? (
                  <div style={{ padding: '0.75rem 0.9rem', fontSize: '0.82rem', color: '#bbb' }}>No Aidey drafts yet</div>
                ) : aideyDrafts.map(draft => (
                  <div
                    key={draft.id}
                    onClick={() => setSelected(draft)}
                    style={{
                      ...styles.emailRow,
                      background: selected?.id === draft.id ? '#f0f4ff' : '#fff',
                      borderLeft: selected?.id === draft.id ? '3px solid #0C2D48' : '3px solid transparent',
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.4rem' }}>
                      <span style={{ fontWeight: 600, fontSize: '0.86rem', color: '#0C2D48', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                        {draft.subject}
                      </span>
                      <span style={{ fontSize: '0.72rem', color: '#aaa', whiteSpace: 'nowrap', flexShrink: 0 }}>{formatDate(draft.date)}</span>
                    </div>
                    <div style={{ fontSize: '0.76rem', color: '#888', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginTop: '0.1rem' }}>
                      {draft.preview}
                    </div>
                    <span style={{ fontSize: '0.6rem', fontWeight: 700, borderRadius: 4, padding: '1px 4px', background: '#eef2ff', color: '#4f6ef7', display: 'inline-block', marginTop: '0.15rem' }}>✦ Aidey draft</span>
                  </div>
                ))}

                {/* Provider Drafts section */}
                <div style={{ padding: '0.35rem 0.9rem', fontSize: '0.72rem', fontWeight: 700, color: '#666', background: '#fafafa', borderBottom: '1px solid #eee', borderTop: '1px solid #eee', marginTop: '0.25rem' }}>
                  Provider Drafts {emails.length > 0 ? `(${emails.length})` : ''}
                </div>
                {loading && emails.length === 0 ? (
                  <div style={{ padding: '1rem', display: 'flex', justifyContent: 'center' }}><div style={styles.spinner} /></div>
                ) : emails.length === 0 ? (
                  <div style={{ padding: '0.75rem 0.9rem', fontSize: '0.82rem', color: '#bbb' }}>No provider drafts</div>
                ) : emails.map(email => (
                  <EmailRow
                    key={`${email.provider}-${email.id}`}
                    email={email}
                    selected={selected?.id === email.id && selected?.provider === email.provider}
                    onSelect={handleSelect}
                    onStar={handleStar}
                    checked={selectedIds.has(`${email.provider}-${email.id}`)}
                    onToggle={() => handleToggleSelect(`${email.provider}-${email.id}`)}
                    onDelete={handleDelete}
                    onRestore={activeFolder === 'deleted' ? handleRestore : undefined}
                  />
                ))}
              </>
            ) : loading && emails.length === 0 ? (
              <div style={styles.centerState}><div style={styles.spinner} /><p style={{ color: '#888', fontSize: '0.85rem' }}>Loading…</p></div>
            ) : error && emails.length === 0 ? (
              <div style={styles.centerState}>
                <p style={{ color: '#A12710', fontSize: '0.88rem' }}>⚠️ {error}</p>
                <button onClick={fetchEmails} style={styles.connectBtn}>Retry</button>
              </div>
            ) : displayEmails.length === 0 ? (
              <div style={styles.centerState}>
                <p style={{ color: '#aaa', fontSize: '0.85rem' }}>
                  {searchQuery ? `No results for "${searchQuery}"` : 'No emails here'}
                </p>
                {searchQuery && (
                  <button onClick={clearSearch} style={{ ...styles.connectBtn, marginTop: '0.5rem' }}>Clear search</button>
                )}
              </div>
            ) : (
              <>
                {/* Select-all row */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.3rem 0.9rem', borderBottom: '1px solid #eee', background: '#fafafa' }}>
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={handleSelectAll}
                    style={{ cursor: 'pointer', accentColor: '#0C2D48', width: 14, height: 14 }}
                  />
                  <span style={{ fontSize: '0.72rem', color: '#aaa' }}>Select all</span>
                </div>
                {displayEmails.map(email => (
                  <EmailRow
                    key={`${email.provider}-${email.id}`}
                    email={email}
                    selected={selected?.id === email.id && selected?.provider === email.provider}
                    onSelect={handleSelect}
                    onStar={handleStar}
                    checked={selectedIds.has(`${email.provider}-${email.id}`)}
                    onToggle={() => handleToggleSelect(`${email.provider}-${email.id}`)}
                    onDelete={handleDelete}
                    onRestore={activeFolder === 'deleted' ? handleRestore : undefined}
                  />
                ))}
              </>
            )}
          </div>

          {pagination && pagination.pages > 1 && (
            <div style={isMobile ? { ...styles.paginationBar, flexDirection: 'row-reverse' } : styles.paginationBar}>
              <span style={styles.paginationCount}>{pagination.total} emails</span>
              <div style={styles.paginationControls}>
                <button
                  onClick={() => { fetchEmails(currentPage - 1, searchQuery, activeProvider); if (emailListRef.current) emailListRef.current.scrollTop = 0; }}
                  disabled={currentPage <= 1}
                  style={{ ...styles.paginationBtn, opacity: currentPage <= 1 ? 0.4 : 1 }}
                >
                  ← Prev
                </button>
                <span style={styles.paginationInfo}>{currentPage} / {pagination.pages}</span>
                <button
                  onClick={() => { fetchEmails(currentPage + 1, searchQuery, activeProvider); if (emailListRef.current) emailListRef.current.scrollTop = 0; }}
                  disabled={currentPage >= pagination.pages}
                  style={{ ...styles.paginationBtn, opacity: currentPage >= pagination.pages ? 0.4 : 1 }}
                >
                  Next →
                </button>
              </div>
            </div>
          )}
        </div>

        {/* ── Right panel: detail ── */}
        <div className={`email-detail-pane${isMobile && mobileDetailOpen ? ' email-detail-pane--mobile-open' : ''}`} style={styles.rightPanel}>
          {!selected ? (
            <div style={styles.centerState}>
              <div style={{ fontSize: '2.5rem' }}>✉️</div>
              <p style={{ color: '#aaa', fontSize: '0.88rem', marginTop: '0.75rem' }}>Select an email to read it</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
              {/* Mobile-only back header */}
              <div className="email-mobile-back-header">
                <button
                  onClick={handleMobileBack}
                  aria-label="Back to inbox"
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0.4rem 0.75rem', fontSize: '0.875rem', fontWeight: 600, color: '#0C2D48', flexShrink: 0, lineHeight: 1, fontFamily: 'inherit' }}
                >
                  ← Inbox
                </button>
                <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '0.875rem', fontWeight: 600, color: '#0C2D48', paddingRight: '0.75rem' }}>
                  {selected.from || selected.fromEmail || 'Email'}
                </span>
              </div>
              {/* Detail header */}
              <div style={styles.detailHeader}>
                <h2 style={styles.detailSubject}>{selected.subject}</h2>
                <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                  {selected.folder !== 'sent' && selected.provider !== 'aidey' && (
                    <button onClick={() => setShowReply(r => !r)} style={styles.actionBtn}>
                      ↩ Reply
                    </button>
                  )}
                  {selected.provider === 'aidey' && (
                    <button
                      onClick={() => {
                        setShowReply(true);
                        setReplyInitialBody(selected.draftBody || '');
                        setReplyInitialSubject(selected.subject || '');
                      }}
                      style={styles.actionBtn}
                    >
                      ✦ Send draft
                    </button>
                  )}
                  {selected.folder !== 'sent' && selected.provider !== 'aidey' && (
                    <button onClick={() => handleStar(selected)} style={{ ...styles.actionBtn, color: selected.isStarred ? '#E4A010' : '#555' }}>
                      {selected.isStarred ? '★ Starred' : '☆ Star'}
                    </button>
                  )}
                  {selected.provider !== 'aidey' && (
                    <button onClick={() => handleAskAidey(selected)} style={styles.aideyBtn}>
                      🤖 Ask Aidey
                    </button>
                  )}
                </div>
              </div>

              {/* Sender / recipient meta */}
              <div style={styles.detailMeta}>
                {selected.folder === 'sent' ? (
                  <div style={{ fontSize: '0.85rem' }}>
                    <strong>To:</strong> {selected.to || selected.toEmail || ''}
                  </div>
                ) : selected.provider === 'aidey' ? (
                  <div style={{ fontSize: '0.85rem' }}>
                    <strong>Draft reply to:</strong> {selected.to || selected.fromEmail || ''}
                  </div>
                ) : (
                  <>
                    <div style={{ fontSize: '0.85rem' }}>
                      <strong>From:</strong> {selected.from || selected.fromEmail}
                    </div>
                    <div style={{ fontSize: '0.82rem', color: '#666' }}>
                      <strong>To:</strong> {selected.to || '(you)'}
                    </div>
                  </>
                )}
                <div style={{ fontSize: '0.78rem', color: '#aaa' }}>{formatDate(selected.date)}</div>

                {/* CRM contact card — not shown for sent or aidey drafts */}
                {selected.fromEmail && selected.folder !== 'sent' && selected.provider !== 'aidey' && (
                  <ContactCard
                    fromEmail={selected.fromEmail}
                    fromName={selected.fromName}
                    onAddToCrm={() => handleAddToCrm(selected)}
                  />
                )}

                {/* Sender automation gear */}
                {selected.fromEmail && selected.folder !== 'sent' && selected.provider !== 'aidey' && Object.keys(automationConfigs).some(k => automationConfigs[k]?.enabled) && (
                  <div style={{ position: 'relative', marginTop: '0.5rem' }}>
                    <button
                      onClick={() => setSenderAutoPopup(p => p === selected.id ? null : selected.id)}
                      title="Automation settings for this sender"
                      style={{ background: 'none', border: '1px solid #e0e0e0', borderRadius: 6, padding: '0.25rem 0.6rem', cursor: 'pointer', fontSize: '0.78rem', color: '#666', fontFamily: "'Raleway', sans-serif" }}
                    >
                      ⚙ Automation
                    </button>
                    {senderAutoPopup === selected.id && (
                      <div style={{ position: 'absolute', top: '110%', left: 0, zIndex: 200, background: '#fff', border: '1px solid #e0e0e0', borderRadius: 10, boxShadow: '0 4px 18px rgba(0,0,0,0.13)', padding: '0.85rem 1rem', minWidth: 260 }}>
                        <div style={{ fontWeight: 700, fontSize: '0.84rem', color: '#0C2D48', marginBottom: '0.6rem', fontFamily: "'Raleway', sans-serif" }}>
                          Automation for {selected.fromName || selected.fromEmail}
                        </div>
                        {Object.entries(automationConfigs).filter(([, c]) => c.enabled).map(([intentType, config]) => {
                          const intentLabel = { meeting_request: 'Meeting requests', invoice_query: 'Invoice queries', po_submission: 'Purchase orders', business_enquiry: 'Business enquiries', spam_scammer: 'Spam' }[intentType] || intentType;
                          return (
                            <div key={intentType} style={{ marginBottom: '0.5rem' }}>
                              <div style={{ fontSize: '0.78rem', color: '#555', marginBottom: '0.25rem', fontFamily: "'Raleway', sans-serif" }}>{intentLabel}</div>
                              <div style={{ display: 'flex', gap: '0.4rem' }}>
                                <button
                                  onClick={() => {
                                    api('/api/email/automation/senders', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ senderEmail: selected.fromEmail, senderName: selected.fromName, intentType, enabled: false }) });
                                    setSenderAutoPopup(null);
                                    showToast('Automation blocked for this sender');
                                  }}
                                  style={{ fontSize: '0.76rem', padding: '0.25rem 0.55rem', border: '1px solid #e0e0e0', borderRadius: 5, cursor: 'pointer', background: '#fff', fontFamily: "'Raleway', sans-serif" }}
                                >
                                  Never automate
                                </button>
                                <button
                                  onClick={() => {
                                    api('/api/email/automation/senders', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ senderEmail: selected.fromEmail, senderName: selected.fromName, intentType, enabled: true, autoSend: true }) });
                                    setSenderAutoPopup(null);
                                    showToast('Always auto-send for this sender');
                                  }}
                                  style={{ fontSize: '0.76rem', padding: '0.25rem 0.55rem', border: '1px solid #e0e0e0', borderRadius: 5, cursor: 'pointer', background: '#fff', fontFamily: "'Raleway', sans-serif" }}
                                >
                                  Always auto-send
                                </button>
                              </div>
                            </div>
                          );
                        })}
                        <button onClick={() => setSenderAutoPopup(null)} style={{ marginTop: '0.35rem', background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.78rem', color: '#aaa', fontFamily: "'Raleway', sans-serif" }}>Close</button>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Body */}
              <div style={{ flex: 1, overflow: 'auto', position: 'relative' }}>
                {selected.provider === 'aidey' ? (
                  <div style={{ padding: '1rem', whiteSpace: 'pre-wrap', fontSize: '0.9rem', color: '#333', lineHeight: 1.6 }}>
                    {selected.draftBody || '(empty draft)'}
                  </div>
                ) : (
                  <iframe
                    ref={iframeRef}
                    title="Email body"
                    sandbox=""
                    srcDoc={`<!DOCTYPE html><html><head><meta charset="utf-8"><style>body{font-family:system-ui,sans-serif;font-size:14px;color:#222;padding:1rem;margin:0}a{color:#0C2D48}img{max-width:100%;height:auto}</style></head><body>${DOMPurify.sanitize(selected.body || '<p style="color:#aaa">No content</p>')}</body></html>`}
                    style={{ width: '100%', height: '100%', border: 'none', minHeight: 300 }}
                  />
                )}
              </div>

              {/* Aidey draft — not shown for sent or aidey-draft items */}
              {!showReply && selected.folder !== 'sent' && selected.provider !== 'aidey' && selected.draftBody && !dismissedDrafts.has(selected.id) && (
                <AideyDraftCard
                  key={selected.id}
                  email={selected}
                  onSend={handleSend}
                  onDismiss={() => handleDismissDraft(selected.id)}
                  sending={replying}
                />
              )}
              {!showReply && selected.folder !== 'sent' && selected.provider !== 'aidey' && !selected.draftBody && !dismissedDrafts.has(selected.id) &&
                (selected.triageCategory === 'urgent' || selected.triageCategory === 'actionable') && (
                <AideyDraftPending />
              )}

              {/* Automation nudge — shown when urgent/actionable and no automation is configured */}
              {!showReply && selected.folder !== 'sent' && selected.provider !== 'aidey' &&
                (selected.triageCategory === 'urgent' || selected.triageCategory === 'actionable') &&
                !Object.keys(automationConfigs).some(k => automationConfigs[k]?.enabled) && (
                <div style={{ margin: '0.75rem 1.5rem', padding: '0.65rem 1rem', background: '#f0f4ff', border: '1px solid #c9d8f8', borderRadius: 8, fontSize: '0.82rem', color: '#2c4da0', fontFamily: "'Raleway', sans-serif", display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem' }}>
                  <span>✦ Aidey can automate replies to emails like this.</span>
                  <button
                    onClick={() => typeof setFilter === 'function' && setFilter('Settings')}
                    style={{ background: 'none', border: '1px solid #c9d8f8', borderRadius: 5, padding: '0.25rem 0.6rem', cursor: 'pointer', fontSize: '0.78rem', color: '#2c4da0', fontFamily: "'Raleway', sans-serif", whiteSpace: 'nowrap' }}
                  >
                    Set up →
                  </button>
                </div>
              )}

              {/* Reply composer */}
              {showReply && (
                <ReplyComposer
                  key={replyInitialBody || 'manual'}
                  email={selected}
                  onSend={handleSend}
                  onClose={() => { setShowReply(false); setReplyInitialBody(''); setReplyInitialSubject(''); }}
                  onWriteWithAidey={handleWriteWithAidey}
                  sending={replying}
                  initialBody={replyInitialBody}
                  initialSubject={replyInitialSubject || undefined}
                />
              )}
            </div>
          )}
        </div>
      </div>

      {/* Compose (new email) */}
      {showCompose && (
        <ComposeModal
          connections={activeConnections}
          onSend={handleCompose}
          onClose={() => setShowCompose(false)}
          sending={composing}
        />
      )}

      {/* Toast */}
      {toast && (
        <div style={{
          ...styles.toast,
          background: toast.type === 'error' ? '#A12710' : '#0C2D48',
          display: 'flex', alignItems: 'center', gap: '0.75rem',
        }}>
          <span>{toast.msg}</span>
          {toast.showUndo && (
            <button
              onClick={handleUndo}
              style={{ background: 'rgba(255,255,255,0.2)', border: '1px solid rgba(255,255,255,0.4)', color: '#fff', cursor: 'pointer', padding: '0.18rem 0.55rem', borderRadius: 4, fontSize: '0.78rem', fontFamily: "'Raleway', sans-serif", flexShrink: 0 }}
            >
              Undo
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────

const styles = {
  root: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    background: '#fff',
    fontFamily: "'Raleway', sans-serif",
  },
  panels: {
    display: 'flex',
    flex: 1,
    minHeight: 0,
    overflow: 'hidden',
  },
  leftPanel: {
    width: 220,
    flexShrink: 0,
    background: '#fff',
    borderRight: '1px solid #e8e8e8',
    display: 'flex',
    flexDirection: 'column',
    padding: '0.5rem 0',
    overflowY: 'auto',
  },
  composeBtn: {
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem',
    width: '100%', margin: '0 0 0.6rem', padding: '0.6rem 0.75rem',
    background: '#0C2D48', color: '#fff', border: 'none', borderRadius: 10,
    fontFamily: "'Raleway', sans-serif", fontWeight: 700, fontSize: '0.86rem', cursor: 'pointer',
  },
  leftHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0.5rem 0.9rem 0.3rem',
  },
  searchBar: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.35rem',
    padding: '0.45rem 0.75rem',
    borderBottom: '1px solid #eee',
    background: '#fff',
    flexShrink: 0,
  },
  searchIcon: {
    fontSize: '1rem',
    color: '#bbb',
    lineHeight: 1,
    flexShrink: 0,
  },
  searchInput: {
    flex: 1,
    border: 'none',
    background: 'transparent',
    outline: 'none',
    fontSize: '16px', // prevents iOS auto-zoom on focus
    fontFamily: "'Raleway', sans-serif",
    color: '#333',
    minHeight: '44px',
    padding: 0,
    WebkitAppearance: 'none',
  },
  searchClear: {
    background: 'none',
    border: 'none',
    color: '#bbb',
    cursor: 'pointer',
    fontSize: '0.85rem',
    padding: '0.25rem',
    lineHeight: 1,
    flexShrink: 0,
    minWidth: '24px',
    minHeight: '24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  middlePanel: {
    width: 360,
    flexShrink: 0,
    borderRight: '1px solid #e8e8e8',
    background: '#fff',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  bulkBar: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.35rem',
    padding: '0.35rem 0.75rem',
    borderBottom: '1px solid #e0e0e0',
    background: '#f5f7ff',
    flexShrink: 0,
    flexWrap: 'wrap',
  },
  trashBar: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '0.4rem 0.75rem', borderBottom: '1px solid #f0d0d0', background: '#fff7f7', flexShrink: 0,
  },
  trashNote: { fontSize: '0.76rem', color: '#a06060', lineHeight: 1.4 },
  emptyTrashBtn: {
    background: '#fff', border: '1px solid #e0a0a0', color: '#c0392b', borderRadius: 8,
    padding: '0.35rem 0.8rem', cursor: 'pointer', fontFamily: "'Raleway', sans-serif",
    fontWeight: 700, fontSize: '0.78rem',
  },
  bulkBtn: {
    background: 'none',
    border: '1px solid #dde',
    borderRadius: 4,
    cursor: 'pointer',
    fontSize: '0.75rem',
    padding: '0.18rem 0.5rem',
    color: '#444',
    fontFamily: "'Raleway', sans-serif",
    whiteSpace: 'nowrap',
  },
  emailRows: {
    flex: 1,
    overflowY: 'auto',
    minHeight: 0,
  },
  rightPanel: {
    flex: 1,
    minWidth: 0,
    background: '#fff',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  triageBar: {
    background: '#fff',
    borderBottom: '1px solid #e8e8e8',
    padding: '0.5rem 0.9rem',
    flexShrink: 0,
  },
  folderBtn: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    padding: '0.5rem 0.9rem',
    background: 'none',
    border: 'none',
    textAlign: 'left',
    fontSize: '0.85rem',
    cursor: 'pointer',
    color: '#333',
    fontFamily: "'Raleway', sans-serif",
  },
  folderActive: {
    background: '#f0f4ff',
    color: '#0C2D48',
    fontWeight: 700,
  },
  sectionLabel: {
    fontSize: '0.68rem',
    fontWeight: 700,
    color: '#aaa',
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    padding: '0.75rem 0.9rem 0.25rem',
  },
  unreadBadge: {
    background: '#0C2D48',
    color: '#fff',
    borderRadius: 10,
    fontSize: '0.68rem',
    fontWeight: 700,
    padding: '0.1rem 0.4rem',
    minWidth: 18,
    textAlign: 'center',
  },
  emailRow: {
    padding: '0.65rem 0.9rem',
    borderBottom: '1px solid #f0f0f0',
    cursor: 'pointer',
    transition: 'background 0.1s',
  },
  unreadDot: {
    width: 7,
    height: 7,
    borderRadius: '50%',
    background: '#0C2D48',
    flexShrink: 0,
  },
  aideyDraftOuter: {
    margin: '0.75rem 1rem 0',
    borderRadius: 8,
    border: '1.5px solid #c7d0f8',
    background: '#f5f7ff',
    overflow: 'hidden',
    flexShrink: 0,
  },
  aideyDraftBar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    padding: '0 0.85rem',
    minHeight: 44,
    background: '#eef1ff',
    border: 'none',
    borderBottom: '1px solid transparent',
    cursor: 'pointer',
    fontFamily: "'Raleway', sans-serif",
    textAlign: 'left',
    gap: '0.5rem',
  },
  aideySparkle: {
    color: '#4f6ef7',
    fontSize: '0.85rem',
    flexShrink: 0,
  },
  aideyDraftExpanded: {
    padding: '0.75rem 0.85rem 0.65rem',
    borderTop: '1px solid #dde2fb',
  },
  aideyDraftTextarea: {
    width: '100%',
    resize: 'vertical',
    border: '1px solid #d4d8f0',
    borderRadius: 6,
    padding: '0.6rem 0.7rem',
    fontSize: '1rem', // 16px — iOS zoom prevention
    lineHeight: 1.55,
    color: '#222',
    fontFamily: "'Raleway', sans-serif",
    boxSizing: 'border-box',
    outline: 'none',
    transition: 'background 0.15s',
  },
  aideyDraftActions: {
    display: 'flex',
    gap: '0.5rem',
    marginTop: '0.6rem',
    flexWrap: 'wrap',
  },
  aideyDraftSendBtn: {
    background: '#0C2D48',
    color: '#fff',
    border: 'none',
    borderRadius: 6,
    cursor: 'pointer',
    fontSize: '0.82rem',
    fontFamily: "'Raleway', sans-serif",
    fontWeight: 700,
    minHeight: 44,
    padding: '0 1.1rem',
    flex: '1 1 auto',
  },
  aideyDraftEditBtn: {
    background: '#fff',
    color: '#0C2D48',
    border: '1.5px solid #c7d0f8',
    borderRadius: 6,
    cursor: 'pointer',
    fontSize: '0.82rem',
    fontFamily: "'Raleway', sans-serif",
    fontWeight: 600,
    minHeight: 44,
    padding: '0 0.9rem',
    flex: '1 1 auto',
  },
  aideyDraftDismissBtn: {
    background: 'none',
    color: '#999',
    border: '1px solid #dde2fb',
    borderRadius: 6,
    cursor: 'pointer',
    fontSize: '0.78rem',
    fontFamily: "'Raleway', sans-serif",
    minHeight: 44,
    padding: '0 0.75rem',
    flex: '0 0 auto',
  },
  aideyDraftPending: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    margin: '0.75rem 1rem 0',
    padding: '0.55rem 0.85rem',
    borderRadius: 8,
    border: '1px dashed #c7d0f8',
    background: '#f8f9ff',
    color: '#8891c8',
    fontSize: '0.78rem',
    fontStyle: 'italic',
    flexShrink: 0,
  },
  detailHeader: {
    padding: '0.9rem 1rem 0.6rem',
    borderBottom: '1px solid #f0f0f0',
  },
  detailSubject: {
    margin: '0 0 0.65rem',
    fontSize: '1rem',
    fontWeight: 700,
    color: '#0C2D48',
    fontFamily: "'Poppins', sans-serif",
    lineHeight: 1.3,
  },
  detailMeta: {
    padding: '0.6rem 1rem 0.5rem',
    borderBottom: '1px solid #f0f0f0',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.2rem',
  },
  actionBtn: {
    padding: '0.35rem 0.8rem',
    background: '#f3f3f3',
    border: '1px solid #e0e0e0',
    borderRadius: 6,
    fontSize: '0.82rem',
    cursor: 'pointer',
    fontWeight: 600,
    fontFamily: "'Raleway', sans-serif",
    color: '#333',
  },
  aideyBtn: {
    padding: '0.35rem 0.8rem',
    background: '#E4E69B',
    border: 'none',
    borderRadius: 6,
    fontSize: '0.82rem',
    cursor: 'pointer',
    fontWeight: 700,
    fontFamily: "'Raleway', sans-serif",
    color: '#0C2D48',
  },
  contactCard: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    background: '#fafafa',
    border: '1px solid #e8e8e8',
    borderRadius: 8,
    padding: '0.45rem 0.7rem',
    marginTop: '0.35rem',
    flexWrap: 'wrap',
  },
  addCrmBtn: {
    padding: '0.2rem 0.6rem',
    background: '#E4E69B',
    border: 'none',
    borderRadius: 5,
    fontSize: '0.75rem',
    fontWeight: 700,
    cursor: 'pointer',
    fontFamily: "'Raleway', sans-serif",
    color: '#0C2D48',
    marginLeft: 'auto',
  },
  triageToggle: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontSize: '0.82rem',
    fontWeight: 700,
    color: '#0C2D48',
    padding: '0.15rem 0.3rem',
    fontFamily: "'Raleway', sans-serif",
  },
  triageChip: {
    padding: '0.2rem 0.6rem',
    border: 'none',
    borderRadius: 12,
    fontSize: '0.78rem',
    cursor: 'pointer',
    fontFamily: "'Raleway', sans-serif",
  },
  triageRunBtn: {
    marginLeft: 'auto',
    padding: '0.25rem 0.75rem',
    background: '#0C2D48',
    color: '#fff',
    border: 'none',
    borderRadius: 6,
    fontSize: '0.78rem',
    fontWeight: 700,
    cursor: 'pointer',
    fontFamily: "'Raleway', sans-serif",
  },
  replyComposer: {
    borderTop: '1px solid #e8e8e8',
    background: '#fff',
    flexShrink: 0,
    maxHeight: 320,
    overflowY: 'auto',
  },
  replyHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0.5rem 0.9rem 0.4rem',
    borderBottom: '1px solid #f0f0f0',
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontSize: '1rem',
    color: '#888',
  },
  replyLabel: {
    fontSize: '0.78rem',
    fontWeight: 600,
    color: '#888',
    minWidth: 44,
  },
  replyInput: {
    flex: 1,
    padding: '0.3rem 0.5rem',
    border: '1px solid #e0e0e0',
    borderRadius: 5,
    fontSize: '0.85rem',
    fontFamily: "'Raleway', sans-serif",
    outline: 'none',
  },
  replyTextarea: {
    width: '100%',
    padding: '0.5rem',
    border: '1px solid #e0e0e0',
    borderRadius: 6,
    fontSize: '0.85rem',
    fontFamily: "'Raleway', sans-serif",
    resize: 'vertical',
    boxSizing: 'border-box',
    outline: 'none',
  },
  aideyDraftBtn: {
    padding: '0.35rem 0.75rem',
    background: '#E4E69B',
    border: 'none',
    borderRadius: 6,
    fontSize: '0.82rem',
    fontWeight: 700,
    cursor: 'pointer',
    fontFamily: "'Raleway', sans-serif",
    color: '#0C2D48',
  },
  cancelBtn: {
    padding: '0.35rem 0.75rem',
    background: '#f3f3f3',
    border: '1px solid #e0e0e0',
    borderRadius: 6,
    fontSize: '0.82rem',
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: "'Raleway', sans-serif",
    color: '#555',
  },
  sendBtn: {
    padding: '0.35rem 0.9rem',
    background: '#0C2D48',
    color: '#fff',
    border: 'none',
    borderRadius: 6,
    fontSize: '0.82rem',
    fontWeight: 700,
    cursor: 'pointer',
    fontFamily: "'Raleway', sans-serif",
  },
  refreshBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontSize: '1rem',
    color: '#888',
    padding: '0 2px',
  },
  centerState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    padding: '2rem',
    textAlign: 'center',
  },
  spinner: {
    width: 28,
    height: 28,
    border: '3px solid #e8e8e8',
    borderTop: '3px solid #0C2D48',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
  },
  connectBtn: {
    padding: '0.65rem 1.5rem',
    background: '#0C2D48',
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    fontWeight: 700,
    fontSize: '0.95rem',
    cursor: 'pointer',
    fontFamily: "'Raleway', sans-serif",
  },
  trustPrompt: {
    background: '#fffbe6',
    border: '1px solid #E4E69B',
    borderLeft: '4px solid #0C2D48',
    padding: '0.75rem 1rem',
    display: 'flex',
    gap: '1rem',
    alignItems: 'flex-start',
    flexShrink: 0,
    flexWrap: 'wrap',
  },
  trustAcceptBtn: {
    padding: '0.35rem 0.85rem',
    background: '#0C2D48',
    color: '#fff',
    border: 'none',
    borderRadius: 6,
    fontSize: '0.78rem',
    fontWeight: 700,
    cursor: 'pointer',
    fontFamily: "'Raleway', sans-serif",
    whiteSpace: 'nowrap',
  },
  trustDeclineBtn: {
    padding: '0.35rem 0.85rem',
    background: '#fff',
    color: '#555',
    border: '1px solid #ddd',
    borderRadius: 6,
    fontSize: '0.78rem',
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: "'Raleway', sans-serif",
    whiteSpace: 'nowrap',
  },
  trustLaterBtn: {
    padding: '0.35rem 0.5rem',
    background: 'none',
    color: '#aaa',
    border: 'none',
    fontSize: '0.75rem',
    cursor: 'pointer',
    fontFamily: "'Raleway', sans-serif",
    whiteSpace: 'nowrap',
  },
  toast: {
    position: 'fixed',
    bottom: '1.5rem',
    left: '50%',
    transform: 'translateX(-50%)',
    color: '#fff',
    padding: '0.6rem 1.3rem',
    borderRadius: 8,
    fontWeight: 600,
    fontSize: '0.88rem',
    zIndex: 2000,
    boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
    whiteSpace: 'nowrap',
  },
  paginationBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0.5rem 0.75rem',
    borderTop: '1px solid #e8e8e8',
    background: '#fafafa',
    flexShrink: 0,
  },
  paginationCount: {
    fontSize: '0.72rem',
    color: '#888',
  },
  paginationControls: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  },
  paginationInfo: {
    fontSize: '0.75rem',
    color: '#555',
    fontWeight: 600,
    minWidth: '3.5rem',
    textAlign: 'center',
  },
  paginationBtn: {
    background: 'none',
    border: '1px solid #dde',
    borderRadius: 4,
    cursor: 'pointer',
    fontSize: '0.75rem',
    padding: '0.25rem 0.6rem',
    color: '#0C2D48',
    fontFamily: "'Raleway', sans-serif",
    fontWeight: 600,
    minHeight: 44,
    minWidth: 44,
  },
};
